// Sugalarm — forward-projection math (extracted cycle 186 from the cycle-180
// in-chart implementation so the web Dashboard's Live focus panel hero and the
// chart's cone consume ONE source of truth and can never disagree).
//
// A straight-line least-squares trend over the last ~4–6 readings, projected
// forward. This is a PRESENTATION HEURISTIC — explicitly NOT a calibrated
// confidence band, no IOB/COB, no closed-loop. The numbers here are byte-
// identical to cycle 180's in-chart computation (pure refactor).

export interface ProjectionModel {
  /** Timestamp (ms) of the most recent reading — the projection origin. */
  t0: number;
  /** Trend slope in mg/dL per minute. */
  slope: number;
  /** Residual scatter of the fit (drives how fast the cone widens). */
  rmse: number;
  /** Value (mg/dL) of the most recent reading. */
  lastV: number;
}

/** Least-squares trend over the last ~4–6 readings. Null with < 3 readings or
 *  a degenerate (zero-variance) x-axis. Inputs are the same `{timestamp, sgv}`
 *  shape the chart already holds. */
export function computeProjectionModel(
  readings: ReadonlyArray<{ timestamp: string; sgv: number }>,
): ProjectionModel | null {
  if (readings.length < 3) return null;
  const k = Math.min(6, readings.length);
  const pts = readings.slice(-k).map((r) => ({
    t: new Date(r.timestamp).getTime(),
    v: r.sgv,
  }));
  const t0 = pts[pts.length - 1]!.t;
  const xs = pts.map((p) => (p.t - t0) / 60000); // minutes; last = 0
  const ys = pts.map((p) => p.v);
  const n = xs.length;
  const xbar = xs.reduce((a, b) => a + b, 0) / n;
  const ybar = ys.reduce((a, b) => a + b, 0) / n;
  let num = 0;
  let den = 0;
  for (let i = 0; i < n; i++) {
    num += (xs[i]! - xbar) * (ys[i]! - ybar);
    den += (xs[i]! - xbar) ** 2;
  }
  if (den === 0) return null;
  const slope = num / den; // mg/dL per minute
  const intercept = ybar - slope * xbar;
  let ss = 0;
  for (let i = 0; i < n; i++) {
    const fit = intercept + slope * xs[i]!;
    ss += (ys[i]! - fit) ** 2;
  }
  const rmse = Math.sqrt(ss / n);
  return { t0, slope, rmse, lastV: ys[ys.length - 1]! };
}

/** Projected centre value `minutes` ahead (unclamped). */
export function projectedCenter(m: ProjectionModel, minutes: number): number {
  return m.lastV + m.slope * minutes;
}

/** Projected value `minutes` ahead, clamped to the plausible CGM range —
 *  the exact figure the cone's "projected · not for dosing" label shows. */
export function predictedValue(m: ProjectionModel, minutes: number): number {
  return Math.max(20, Math.min(600, Math.round(projectedCenter(m, minutes))));
}

/** Mirror of the chart's stale gate: no projection off a sensor whose most
 *  recent reading is older than the horizon (or implausibly in the future). */
export function isProjectionStale(
  m: ProjectionModel,
  nowMs: number,
  projectMinutes: number,
): boolean {
  const staleMs = nowMs - m.t0;
  return staleMs < -60_000 || staleMs > projectMinutes * 60_000;
}
